import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { AlertCircleIcon, InboxIcon, SearchXIcon, WifiOffIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { isAppError, type AppError } from "@/api/problem"
import { cn } from "@/lib/cn"

/**
 * The six states every screen must have (PDD §11.2). They live together
 * because the distinctions between them are the point, and splitting them
 * across files is how those distinctions get lost.
 */

function Shell({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode
  title: string
  body?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <div className="text-muted-foreground" aria-hidden>
        {icon}
      </div>
      <h2 className="text-base font-medium">{title}</h2>
      {body ? <div className="max-w-sm text-sm text-muted-foreground">{body}</div> : null}
      {action}
    </div>
  )
}

/** State 2: nothing yet. An onboarding moment -- offer the first action. */
export function EmptyState({
  title,
  body,
  action,
}: {
  title?: string
  body?: ReactNode
  action?: ReactNode
}) {
  const { t } = useTranslation("common")
  return (
    <Shell
      icon={<InboxIcon className="size-8" />}
      title={title ?? t("states.emptyTitle")}
      body={body}
      action={action}
    />
  )
}

/**
 * State 3: nothing MATCHED. Visibly different from "nothing yet" -- conflating
 * the two reliably makes people think the application is broken.
 */
export function FilteredEmptyState({
  activeFilters,
  onClear,
}: {
  activeFilters?: string[]
  onClear?: () => void
}) {
  const { t } = useTranslation("common")
  return (
    <Shell
      icon={<SearchXIcon className="size-8" />}
      title={t("states.filteredEmptyTitle")}
      body={
        <>
          <p>{t("states.filteredEmptyBody")}</p>
          {activeFilters && activeFilters.length > 0 ? (
            <p className="mt-2 text-xs">{activeFilters.join(" · ")}</p>
          ) : null}
        </>
      }
      action={
        onClear ? (
          <Button variant="outline" size="sm" onClick={onClear}>
            {t("actions.clearFilters")}
          </Button>
        ) : null
      }
    />
  )
}

/**
 * State 5. Chooses its copy from the error's KIND, so "you are offline" and
 * "we are broken" are visibly different -- they call for different reactions
 * (PDD §9.4). Always exposes requestId (NFR-DSH-15).
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation("common")
  const err: AppError | null = isAppError(error) ? error : null
  const offline = err?.kind === "network"

  return (
    <Shell
      icon={offline ? <WifiOffIcon className="size-8" /> : <AlertCircleIcon className="size-8" />}
      title={offline ? t("states.offlineTitle") : t("states.errorTitle")}
      body={
        <>
          <p>
            {offline
              ? t("states.offlineBody")
              : (err?.detail ?? (err && err.status >= 500 ? t("states.serverErrorBody") : null))}
          </p>
          {err?.requestId ? (
            <p className="mt-3 text-xs">
              {t("states.requestId")}: <code className="numeric">{err.requestId}</code>
            </p>
          ) : null}
        </>
      }
      action={
        onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("actions.retry")}
          </Button>
        ) : null
      }
    />
  )
}

/** State 1: shaped like the content that is coming, so the layout does not jump. */
export function LoadingState({
  variant = "table",
  rows = 6,
  className,
}: {
  variant?: "table" | "form" | "detail" | "grid"
  rows?: number
  className?: string
}) {
  const { t } = useTranslation("common")
  const items = Array.from({ length: rows }, (_, i) => i)

  return (
    <div className={cn("w-full", className)} aria-busy="true" aria-live="polite">
      <span className="sr-only">{t("states.loading")}</span>
      {variant === "grid" ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((i) => (
            <Skeleton key={i} className="aspect-square w-full" />
          ))}
        </div>
      ) : variant === "form" ? (
        <div className="max-w-2xl space-y-6">
          {items.map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
      ) : variant === "detail" ? (
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="space-y-1">
          <Skeleton className="h-10 w-full" />
          {items.map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}
    </div>
  )
}
